import React, { useState, useEffect, useCallback } from 'react';
import { adminApi, Puzzle } from '../services/adminApi';
import Spinner from './Spinner';

interface PuzzlesProps {
    onError?: (error: string) => void;
}

const Puzzles: React.FC<PuzzlesProps> = ({ onError }) => {
    const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
    const [filteredPuzzles, setFilteredPuzzles] = useState<Puzzle[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [puzzleForm, setPuzzleForm] = useState<Partial<Puzzle>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [savedSincePublish, setSavedSincePublish] = useState<Set<string>>(new Set());
    const [showFilter, setShowFilter] = useState(false);
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
        direction: 'first' | 'prev10' | 'prev' | 'next' | 'next10' | 'last';
        newIndex: number;
    } | null>(null);
    // Applied filters (currently active)
    const [appliedCountries, setAppliedCountries] = useState<Set<string>>(new Set());
    const [appliedPhotographers, setAppliedPhotographers] = useState<Set<string>>(new Set());
    const [appliedContentTags, setAppliedContentTags] = useState<Set<string>>(new Set());
    const [appliedVisibleFilter, setAppliedVisibleFilter] = useState<Set<string>>(new Set());
    // Temporary filters (while popup is open)
    const [tempCountries, setTempCountries] = useState<Set<string>>(new Set());
    const [tempPhotographers, setTempPhotographers] = useState<Set<string>>(new Set());
    const [tempContentTags, setTempContentTags] = useState<Set<string>>(new Set());
    const [tempVisibleFilter, setTempVisibleFilter] = useState<Set<string>>(new Set());
    const [hasFailed, setHasFailed] = useState(false);

    const loadPuzzles = useCallback(async () => {
        // Don't retry if we've already encountered an error
        if (hasFailed) {
            return;
        }

        setLoading(true);
        if (onError) {
            onError('');
        }

        try {
            const allPuzzles: Puzzle[] = [];
            let currentToken: string | undefined = undefined;

            // Load all puzzles in batches
            while (true) {
                const response = await adminApi.getPuzzles(100, currentToken);
                if (response.status === 'success' && response.data) {
                    allPuzzles.push(...(response.data.items || []));
                    currentToken = response.data.next;

                    // If there's no next token, we've loaded all puzzles
                    if (!currentToken) {
                        break;
                    }
                } else {
                    const errorMessage = response.message || 'Failed to load puzzles';

                    // Check if this is a non-200 status code - if so, mark it and stop retrying
                    if (response.statusCode && response.statusCode !== 200) {
                        setHasFailed(true);
                    }

                    if (onError) {
                        onError(errorMessage);
                    }
                    break;
                }
            }

            // Sort puzzles by created date in reverse chronological order (newest first)
            allPuzzles.sort((a, b) => {
                const dateA = a.created ? new Date(a.created).getTime() : 0;
                const dateB = b.created ? new Date(b.created).getTime() : 0;
                return dateB - dateA; // Reverse chronological (newest first)
            });

            setPuzzles(allPuzzles);
            setFilteredPuzzles(allPuzzles);
            setCurrentIndex(0);
        } catch (err) {
            const errorMessage = 'Failed to load puzzles';
            setHasFailed(true);
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, [onError, hasFailed]);

    useEffect(() => {
        loadPuzzles();
    }, [loadPuzzles]);

    // Extract unique values for filter options
    const getUniqueCountries = () => {
        const countries = new Set<string>();
        puzzles.forEach(puzzle => {
            if (puzzle.tags?.country) {
                countries.add(puzzle.tags.country);
            }
        });
        return Array.from(countries).sort();
    };

    const getUniquePhotographers = () => {
        const photographers = new Set<string>();
        puzzles.forEach(puzzle => {
            if (puzzle.photographer) {
                photographers.add(puzzle.photographer);
            }
        });
        return Array.from(photographers).sort();
    };

    const getUniqueContentTags = () => {
        const tags = new Set<string>();
        puzzles.forEach(puzzle => {
            if (puzzle.tags?.content) {
                puzzle.tags.content.forEach(tag => {
                    if (tag) {
                        tags.add(tag);
                    }
                });
            }
        });
        return Array.from(tags).sort();
    };

    // Apply filters
    const applyFilters = useCallback(() => {
        const filtered = puzzles.filter(puzzle => {
            // Country filter: OR logic - match any selected country, or pass if none selected
            const countryMatch =
                appliedCountries.size === 0 ||
                (puzzle.tags?.country && appliedCountries.has(puzzle.tags.country));

            // Photographer filter: OR logic - match any selected photographer, or pass if none selected
            const photographerMatch =
                appliedPhotographers.size === 0 ||
                (puzzle.photographer && appliedPhotographers.has(puzzle.photographer));

            // Content tags filter: OR logic - match any selected tag, or pass if none selected
            const contentTagMatch =
                appliedContentTags.size === 0 ||
                (puzzle.tags?.content &&
                    puzzle.tags.content.some(tag => tag && appliedContentTags.has(tag)));

            // Visible/hidden filter: show all if empty or both selected, otherwise filter by visibility
            const visibleMatch =
                appliedVisibleFilter.size === 0 ||
                appliedVisibleFilter.size === 2 ||
                (appliedVisibleFilter.has('visible') && puzzle.visible === true) ||
                (appliedVisibleFilter.has('hidden') && puzzle.visible === false);

            // AND logic between all filter groups
            return countryMatch && photographerMatch && contentTagMatch && visibleMatch;
        });

        setFilteredPuzzles(filtered);
    }, [puzzles, appliedCountries, appliedPhotographers, appliedContentTags, appliedVisibleFilter]);

    // Apply filters whenever applied filters or puzzles change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Reset index if it's out of bounds after filtering
    useEffect(() => {
        if (currentIndex >= filteredPuzzles.length && filteredPuzzles.length > 0) {
            setCurrentIndex(0);
        } else if (filteredPuzzles.length === 0) {
            setCurrentIndex(0);
        }
    }, [filteredPuzzles.length, currentIndex]);

    useEffect(() => {
        if (filteredPuzzles.length > 0 && currentIndex < filteredPuzzles.length) {
            const puzzle = filteredPuzzles[currentIndex];
            setPuzzleForm({
                id: puzzle.id,
                title: puzzle.title || '',
                description: puzzle.description || '',
                photographer: puzzle.photographer || '',
                license: puzzle.license || '',
                source: puzzle.source || '',
                sourceId: puzzle.sourceId || '',
                visible: puzzle.visible ?? true,
                tags: {
                    country: puzzle.tags?.country || '',
                    region: puzzle.tags?.region || '',
                    content: puzzle.tags?.content || [],
                },
            });
            setHasChanges(false);
        } else if (filteredPuzzles.length === 0) {
            // Clear form when filtered list is empty
            setPuzzleForm({
                id: '',
                title: '',
                description: '',
                photographer: '',
                license: '',
                source: '',
                sourceId: '',
                visible: true,
                tags: {
                    country: '',
                    region: '',
                    content: [],
                },
            });
            setHasChanges(false);
        }
    }, [currentIndex, filteredPuzzles]);

    const handlePublishCatalog = async () => {
        if (
            !window.confirm(
                'Are you sure you want to publish the catalog? This will update the published puzzle catalog.'
            )
        ) {
            return;
        }

        setPublishing(true);
        if (onError) {
            onError('');
        }

        try {
            const response = await adminApi.publishCatalog();
            if (response.status === 'success') {
                // Clear saved puzzles set
                setSavedSincePublish(new Set());

                if (onError) {
                    onError('');
                }
                alert(response.message || 'Catalog published successfully');
            } else {
                const errorMessage = response.message || 'Failed to publish catalog';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to publish catalog';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setPublishing(false);
        }
    };

    const handleSave = async () => {
        if (!puzzleForm.id) return;

        setSaving(true);
        if (onError) {
            onError('');
        }

        try {
            const updateRequest: any = {
                title: puzzleForm.title,
                description: puzzleForm.description,
                photographer: puzzleForm.photographer,
                license: puzzleForm.license,
                visible: puzzleForm.visible,
                tags: {
                    country: puzzleForm.tags?.country || '',
                    region: puzzleForm.tags?.region || '',
                    content: puzzleForm.tags?.content || [],
                },
            };

            // Remove empty strings
            Object.keys(updateRequest).forEach(key => {
                if (updateRequest[key] === '') {
                    delete updateRequest[key];
                }
            });

            if (updateRequest.tags) {
                if (updateRequest.tags.country === '') delete updateRequest.tags.country;
                if (updateRequest.tags.region === '') delete updateRequest.tags.region;
                if (!updateRequest.tags.content || updateRequest.tags.content.length === 0) {
                    delete updateRequest.tags.content;
                }
            }

            const response = await adminApi.updatePuzzle(puzzleForm.id, updateRequest);
            if (response.status === 'success' && response.data) {
                // Update the puzzle in the local array by ID
                const updatedPuzzles = puzzles.map(puzzle =>
                    puzzle.id === puzzleForm.id ? response.data! : puzzle
                );
                setPuzzles(updatedPuzzles);
                setHasChanges(false);

                // Add puzzle ID to saved set since last publish
                if (puzzleForm.id) {
                    setSavedSincePublish(prev => new Set(prev).add(puzzleForm.id!));
                }
            } else {
                const errorMessage = response.message || 'Failed to save puzzle';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to save puzzle';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleFieldChange = (field: string, value: any) => {
        setPuzzleForm(prev => {
            if (field.startsWith('tags.')) {
                const tagField = field.split('.')[1];
                return {
                    ...prev,
                    tags: {
                        ...prev.tags,
                        [tagField]: value,
                    },
                };
            }
            return {
                ...prev,
                [field]: value,
            };
        });
        setHasChanges(true);
    };

    const handleContentTagChange = (index: number, value: string) => {
        const contentTags = [...(puzzleForm.tags?.content || [])];
        while (contentTags.length <= index) {
            contentTags.push('');
        }
        contentTags[index] = value;
        // Remove trailing empty strings
        while (contentTags.length > 0 && contentTags[contentTags.length - 1] === '') {
            contentTags.pop();
        }
        handleFieldChange('tags.content', contentTags);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const translateField = async (field: 'title' | 'description') => {
        const fieldValue = field === 'title' ? puzzleForm.title : puzzleForm.description;

        if (!fieldValue || !fieldValue.trim()) {
            if (onError) {
                onError('No text to translate in this field');
            }
            return;
        }

        try {
            // Check if Translator API is available (from @vitalets/google-translate-api or similar)
            if ('Translator' in window && 'LanguageDetector' in window) {
                // @ts-ignore - Translator API may be loaded dynamically
                const detector = await window.LanguageDetector.create();
                // @ts-ignore
                const detectionResult = await detector.detect(fieldValue);
                const sourceLanguage = detectionResult[0].detectedLanguage;

                // @ts-ignore
                const translator = await window.Translator.create({
                    sourceLanguage: sourceLanguage,
                    targetLanguage: 'en',
                });

                // @ts-ignore
                const translatedText = await translator.translate(fieldValue);

                if (field === 'title') {
                    handleFieldChange('title', translatedText);
                } else {
                    handleFieldChange('description', translatedText);
                }

                if (onError) {
                    onError('');
                }
            } else {
                if (onError) {
                    onError(
                        'Translator API not available. Please ensure the translation library is loaded.'
                    );
                }
            }
        } catch (error: any) {
            console.error('Translation error:', error);
            if (onError) {
                onError('Translation failed: ' + (error.message || 'Unknown error'));
            }
        }
    };

    const cleanupField = (field: 'title' | 'description') => {
        const fieldValue = field === 'title' ? puzzleForm.title : puzzleForm.description;

        if (!fieldValue) {
            if (onError) {
                onError('No text to clean in this field');
            }
            return;
        }

        // Remove all backslashes
        const cleanedValue = fieldValue.replace(/\\/g, '');

        if (cleanedValue === fieldValue) {
            if (onError) {
                onError('No backslashes found in this field');
            }
            return;
        }

        if (field === 'title') {
            handleFieldChange('title', cleanedValue);
        } else {
            handleFieldChange('description', cleanedValue);
        }

        if (onError) {
            onError('');
        }
    };

    const navigate = (direction: 'first' | 'prev10' | 'prev' | 'next' | 'next10' | 'last') => {
        if (filteredPuzzles.length === 0) return;

        let newIndex = currentIndex;
        switch (direction) {
            case 'first':
                newIndex = 0;
                break;
            case 'prev10':
                newIndex = Math.max(0, currentIndex - 10);
                break;
            case 'prev':
                newIndex = Math.max(0, currentIndex - 1);
                break;
            case 'next':
                newIndex = Math.min(filteredPuzzles.length - 1, currentIndex + 1);
                break;
            case 'next10':
                newIndex = Math.min(filteredPuzzles.length - 1, currentIndex + 10);
                break;
            case 'last':
                newIndex = filteredPuzzles.length - 1;
                break;
        }

        // If trying to navigate to the same puzzle, do nothing
        if (newIndex === currentIndex) return;

        // If there are unsaved changes, show confirmation dialog
        if (hasChanges) {
            setPendingNavigation({ direction, newIndex });
            setShowUnsavedChangesDialog(true);
            return;
        }

        // No unsaved changes, proceed with navigation
        setCurrentIndex(newIndex);
    };

    const handleUnsavedChangesSave = async () => {
        if (!puzzleForm.id) return;

        setSaving(true);
        if (onError) {
            onError('');
        }

        try {
            const updateRequest: any = {
                title: puzzleForm.title,
                description: puzzleForm.description,
                photographer: puzzleForm.photographer,
                license: puzzleForm.license,
                visible: puzzleForm.visible,
                tags: {
                    country: puzzleForm.tags?.country || '',
                    region: puzzleForm.tags?.region || '',
                    content: puzzleForm.tags?.content || [],
                },
            };

            // Remove empty strings
            Object.keys(updateRequest).forEach(key => {
                if (updateRequest[key] === '') {
                    delete updateRequest[key];
                }
            });

            if (updateRequest.tags) {
                if (updateRequest.tags.country === '') delete updateRequest.tags.country;
                if (updateRequest.tags.region === '') delete updateRequest.tags.region;
                if (!updateRequest.tags.content || updateRequest.tags.content.length === 0) {
                    delete updateRequest.tags.content;
                }
            }

            const response = await adminApi.updatePuzzle(puzzleForm.id, updateRequest);
            if (response.status === 'success' && response.data) {
                // Update the puzzle in the local array by ID
                const updatedPuzzles = puzzles.map(puzzle =>
                    puzzle.id === puzzleForm.id ? response.data! : puzzle
                );
                setPuzzles(updatedPuzzles);
                setHasChanges(false);

                // Add puzzle ID to saved set since last publish
                if (puzzleForm.id) {
                    setSavedSincePublish(prev => new Set(prev).add(puzzleForm.id!));
                }

                // Navigate after successful save
                if (pendingNavigation) {
                    setCurrentIndex(pendingNavigation.newIndex);
                    setPendingNavigation(null);
                }
            } else {
                const errorMessage = response.message || 'Failed to save puzzle';
                if (onError) {
                    onError(errorMessage);
                }
            }
        } catch (err) {
            const errorMessage = 'Failed to save puzzle';
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setSaving(false);
            setShowUnsavedChangesDialog(false);
        }
    };

    const handleUnsavedChangesDiscard = () => {
        setHasChanges(false);
        setShowUnsavedChangesDialog(false);
        if (pendingNavigation) {
            setCurrentIndex(pendingNavigation.newIndex);
            setPendingNavigation(null);
        }
    };

    const handleUnsavedChangesCancel = () => {
        setShowUnsavedChangesDialog(false);
        setPendingNavigation(null);
    };

    const getPuzzleImageUrl = (puzzleId: string) => {
        const gameUrl = import.meta.env.VITE_REACT_APP_GAME_URL || '';
        if (gameUrl) {
            return `${gameUrl}/thumbnails/${puzzleId}_thumb.webp`;
        }
        return `/thumbnails/${puzzleId}_thumb.webp`;
    };

    const handleFilterOpen = () => {
        // Copy applied filters to temporary filters when opening
        setTempCountries(new Set(appliedCountries));
        setTempPhotographers(new Set(appliedPhotographers));
        setTempContentTags(new Set(appliedContentTags));
        setTempVisibleFilter(new Set(appliedVisibleFilter));
        setShowFilter(true);
    };

    const handleFilterApply = () => {
        // Apply temporary filters to applied filters
        setAppliedCountries(new Set(tempCountries));
        setAppliedPhotographers(new Set(tempPhotographers));
        setAppliedContentTags(new Set(tempContentTags));
        setAppliedVisibleFilter(new Set(tempVisibleFilter));
        setShowFilter(false);
    };

    const handleFilterReset = () => {
        // Clear both temporary and applied filters
        setTempCountries(new Set());
        setTempPhotographers(new Set());
        setTempContentTags(new Set());
        setTempVisibleFilter(new Set());
        setAppliedCountries(new Set());
        setAppliedPhotographers(new Set());
        setAppliedContentTags(new Set());
        setAppliedVisibleFilter(new Set());
        setShowFilter(false);
    };

    const handleCountryToggle = (country: string) => {
        setTempCountries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(country)) {
                newSet.delete(country);
            } else {
                newSet.add(country);
            }
            return newSet;
        });
    };

    const handlePhotographerToggle = (photographer: string) => {
        setTempPhotographers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(photographer)) {
                newSet.delete(photographer);
            } else {
                newSet.add(photographer);
            }
            return newSet;
        });
    };

    const handleContentTagToggle = (tag: string) => {
        setTempContentTags(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tag)) {
                newSet.delete(tag);
            } else {
                newSet.add(tag);
            }
            return newSet;
        });
    };

    const handleVisibleToggle = (value: 'visible' | 'hidden') => {
        setTempVisibleFilter(prev => {
            const newSet = new Set(prev);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <div className='space-y-6'>
                <div className='text-center py-8'>
                    <Spinner size='lg' color='blue' className='mx-auto' />
                    <p className='mt-4 text-gray-600'>Loading puzzles...</p>
                </div>
            </div>
        );
    }

    if (puzzles.length === 0) {
        return (
            <div className='space-y-6'>
                <div className='text-center py-8'>
                    <p className='text-gray-600'>No puzzles found</p>
                </div>
            </div>
        );
    }

    const currentPuzzle = filteredPuzzles[currentIndex];
    const puzzleImageUrl = currentPuzzle ? getPuzzleImageUrl(currentPuzzle.id) : '';

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const getWikimediaCommonsUrl = (sourceId: string | undefined): string => {
        if (!sourceId) return '';
        let fileId = sourceId;
        if (!fileId.startsWith('File:')) {
            fileId = `File:${fileId}`;
        }
        return `https://commons.wikimedia.org/wiki/${fileId}`;
    };

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <div className='flex items-center space-x-4'>
                    <h2 className='text-2xl font-bold text-gray-900'>Puzzle Management</h2>
                    <button
                        type='button'
                        onClick={handlePublishCatalog}
                        disabled={publishing}
                        className='px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        {publishing ? 'Publishing...' : 'Publish Catalog'}
                    </button>
                    <button
                        type='button'
                        onClick={handleFilterOpen}
                        className='px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                    >
                        Filter
                    </button>
                    {savedSincePublish.size > 0 && (
                        <span className='text-sm text-gray-600'>
                            ({savedSincePublish.size} saved since last publish)
                        </span>
                    )}
                </div>
                <div className='flex items-center space-x-4'>
                    <span className='text-sm text-gray-600'>
                        Puzzle #{currentIndex + 1} of {filteredPuzzles.length}
                        {filteredPuzzles.length !== puzzles.length && (
                            <span className='text-gray-500'> (of {puzzles.length} total)</span>
                        )}
                    </span>
                </div>
            </div>

            <div className='flex justify-end space-x-2 mb-4'>
                <button
                    type='button'
                    onClick={() => navigate('first')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex === 0}
                >
                    First
                </button>
                <button
                    type='button'
                    onClick={() => navigate('prev10')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex === 0}
                >
                    -10
                </button>
                <button
                    type='button'
                    onClick={() => navigate('prev')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex === 0}
                >
                    -1
                </button>
                <button
                    type='button'
                    onClick={() => navigate('next')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex >= puzzles.length - 1}
                >
                    +1
                </button>
                <button
                    type='button'
                    onClick={() => navigate('next10')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex >= puzzles.length - 1}
                >
                    +10
                </button>
                <button
                    type='button'
                    onClick={() => navigate('last')}
                    className='px-3 py-1 text-sm bg-blue-100 text-gray-700 rounded hover:bg-blue-200'
                    disabled={currentIndex >= puzzles.length - 1}
                >
                    Last
                </button>
            </div>

            <div className='grid grid-cols-2 gap-6'>
                {/* Left Column */}
                <div className='space-y-4'>
                    <div>
                        <div className='flex items-center justify-between mb-1'>
                            <label className='block text-sm font-medium text-gray-700'>TITLE</label>
                            <div className='flex space-x-2'>
                                <button
                                    type='button'
                                    onClick={() => translateField('title')}
                                    className='px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
                                >
                                    Translate
                                </button>
                                <button
                                    type='button'
                                    onClick={() => cleanupField('title')}
                                    className='px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors'
                                >
                                    Cleanup
                                </button>
                            </div>
                        </div>
                        <input
                            type='text'
                            value={puzzleForm.title || ''}
                            onChange={e => handleFieldChange('title', e.target.value)}
                            className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        />
                        {currentPuzzle?.created && (
                            <label className='block text-sm text-gray-600 mt-2'>
                                Uploaded: {formatDate(currentPuzzle.created)}
                            </label>
                        )}
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            Image
                        </label>
                        <div className='border border-gray-300 rounded-md p-4 bg-white min-h-[250px] flex items-center justify-center'>
                            {puzzleImageUrl && (
                                <img
                                    src={puzzleImageUrl}
                                    alt='Puzzle'
                                    className='max-w-full max-h-[250px] object-contain'
                                    onError={e => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            )}
                        </div>
                        {currentPuzzle?.source === 'WikimediaCommons' &&
                            currentPuzzle?.sourceId && (
                                <a
                                    href={getWikimediaCommonsUrl(currentPuzzle.sourceId)}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='block text-sm text-blue-600 hover:text-blue-800 underline mt-2'
                                >
                                    View on Wikimedia Commons
                                </a>
                            )}
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            COUNTRY
                        </label>
                        <input
                            type='text'
                            value={puzzleForm.tags?.country || ''}
                            onChange={e => handleFieldChange('tags.country', e.target.value)}
                            className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        />
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            REGION
                        </label>
                        <input
                            type='text'
                            value={puzzleForm.tags?.region || ''}
                            onChange={e => handleFieldChange('tags.region', e.target.value)}
                            className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        />
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            PHOTOGRAPHER
                        </label>
                        <input
                            type='text'
                            value={puzzleForm.photographer || ''}
                            onChange={e => handleFieldChange('photographer', e.target.value)}
                            className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        />
                    </div>
                </div>

                {/* Right Column */}
                <div className='space-y-4'>
                    <div>
                        <div className='flex items-center justify-between mb-1'>
                            <label className='block text-sm font-medium text-gray-700'>
                                DESCRIPTION
                            </label>
                            <div className='flex space-x-2'>
                                <button
                                    type='button'
                                    onClick={() => translateField('description')}
                                    className='px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors'
                                >
                                    Translate
                                </button>
                                <button
                                    type='button'
                                    onClick={() => cleanupField('description')}
                                    className='px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors'
                                >
                                    Cleanup
                                </button>
                            </div>
                        </div>
                        <textarea
                            value={puzzleForm.description || ''}
                            onChange={e => handleFieldChange('description', e.target.value)}
                            placeholder='Enter description'
                            className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[150px]'
                        />
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            CONTENT TAGS
                        </label>
                        <div className='space-y-2'>
                            {[0, 1, 2, 3, 4].map(index => (
                                <input
                                    key={index}
                                    type='text'
                                    value={puzzleForm.tags?.content?.[index] || ''}
                                    onChange={e => handleContentTagChange(index, e.target.value)}
                                    placeholder='Tag'
                                    className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>ID</label>
                        <div className='flex space-x-2'>
                            <input
                                type='text'
                                value={puzzleForm.id || ''}
                                readOnly
                                className='flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50'
                            />
                            <button
                                type='button'
                                onClick={() => copyToClipboard(puzzleForm.id || '')}
                                className='px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            LICENSE
                        </label>
                        <div className='flex space-x-2'>
                            <input
                                type='text'
                                value={puzzleForm.license || ''}
                                onChange={e => handleFieldChange('license', e.target.value)}
                                className='flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                            />
                            <button
                                type='button'
                                onClick={() => copyToClipboard(puzzleForm.license || '')}
                                className='px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            SOURCE
                        </label>
                        <div className='flex space-x-2'>
                            <input
                                type='text'
                                value={puzzleForm.source || ''}
                                readOnly
                                className='flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50'
                            />
                            <button
                                type='button'
                                onClick={() => copyToClipboard(puzzleForm.source || '')}
                                className='px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                            SOURCEID
                        </label>
                        <div className='flex space-x-2'>
                            <input
                                type='text'
                                value={puzzleForm.sourceId || ''}
                                readOnly
                                className='flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50'
                            />
                            <button
                                type='button'
                                onClick={() => copyToClipboard(puzzleForm.sourceId || '')}
                                className='px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200'
                            >
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className='flex items-center space-x-2'>
                            <input
                                type='checkbox'
                                checked={puzzleForm.visible ?? true}
                                onChange={e => handleFieldChange('visible', e.target.checked)}
                                className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                            />
                            <span className='text-sm font-medium text-gray-700'>VISIBLE</span>
                        </label>
                    </div>

                    <div className='pt-4'>
                        <button
                            type='button'
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            className='w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter Popup */}
            {showFilter && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4'>
                        <div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center'>
                            <h3 className='text-xl font-bold text-gray-900'>Filter Puzzles</h3>
                            <div className='flex space-x-2'>
                                <button
                                    type='button'
                                    onClick={handleFilterReset}
                                    className='px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors'
                                >
                                    Reset
                                </button>
                                <button
                                    type='button'
                                    onClick={handleFilterApply}
                                    className='px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                                >
                                    Apply
                                </button>
                                <button
                                    type='button'
                                    onClick={() => setShowFilter(false)}
                                    className='px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                        <div className='p-6 space-y-6'>
                            {/* Countries */}
                            <div>
                                <h4 className='text-lg font-semibold text-gray-800 mb-3'>
                                    Countries
                                </h4>
                                <div className='max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2'>
                                    {getUniqueCountries().map(country => (
                                        <label
                                            key={country}
                                            className='flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded'
                                        >
                                            <input
                                                type='checkbox'
                                                checked={tempCountries.has(country)}
                                                onChange={() => handleCountryToggle(country)}
                                                className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                            />
                                            <span className='text-sm text-gray-700'>{country}</span>
                                        </label>
                                    ))}
                                    {getUniqueCountries().length === 0 && (
                                        <p className='text-sm text-gray-500'>
                                            No countries available
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Photographers */}
                            <div>
                                <h4 className='text-lg font-semibold text-gray-800 mb-3'>
                                    Photographers
                                </h4>
                                <div className='max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2'>
                                    {getUniquePhotographers().map(photographer => (
                                        <label
                                            key={photographer}
                                            className='flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded'
                                        >
                                            <input
                                                type='checkbox'
                                                checked={tempPhotographers.has(photographer)}
                                                onChange={() =>
                                                    handlePhotographerToggle(photographer)
                                                }
                                                className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                            />
                                            <span className='text-sm text-gray-700'>
                                                {photographer}
                                            </span>
                                        </label>
                                    ))}
                                    {getUniquePhotographers().length === 0 && (
                                        <p className='text-sm text-gray-500'>
                                            No photographers available
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Content Tags */}
                            <div>
                                <h4 className='text-lg font-semibold text-gray-800 mb-3'>
                                    Content Tags
                                </h4>
                                <div className='max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2'>
                                    {getUniqueContentTags().map(tag => (
                                        <label
                                            key={tag}
                                            className='flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded'
                                        >
                                            <input
                                                type='checkbox'
                                                checked={tempContentTags.has(tag)}
                                                onChange={() => handleContentTagToggle(tag)}
                                                className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                            />
                                            <span className='text-sm text-gray-700'>{tag}</span>
                                        </label>
                                    ))}
                                    {getUniqueContentTags().length === 0 && (
                                        <p className='text-sm text-gray-500'>
                                            No content tags available
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Visible/Hidden */}
                            <div>
                                <h4 className='text-lg font-semibold text-gray-800 mb-3'>
                                    Visible/Hidden
                                </h4>
                                <div className='border border-gray-200 rounded-md p-3 space-y-2'>
                                    <label className='flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded'>
                                        <input
                                            type='checkbox'
                                            checked={tempVisibleFilter.has('visible')}
                                            onChange={() => handleVisibleToggle('visible')}
                                            className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                        />
                                        <span className='text-sm text-gray-700'>Visible</span>
                                    </label>
                                    <label className='flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded'>
                                        <input
                                            type='checkbox'
                                            checked={tempVisibleFilter.has('hidden')}
                                            onChange={() => handleVisibleToggle('hidden')}
                                            className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                                        />
                                        <span className='text-sm text-gray-700'>Hidden</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Unsaved Changes Dialog */}
            {showUnsavedChangesDialog && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-white rounded-lg shadow-xl max-w-md w-full m-4'>
                        <div className='p-6'>
                            <h3 className='text-xl font-bold text-gray-900 mb-4'>
                                Unsaved Changes
                            </h3>
                            <p className='text-gray-600 mb-6'>
                                You have unsaved changes. What would you like to do?
                            </p>
                            <div className='flex justify-end space-x-3'>
                                <button
                                    type='button'
                                    onClick={handleUnsavedChangesCancel}
                                    disabled={saving}
                                    className='px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    onClick={handleUnsavedChangesDiscard}
                                    disabled={saving}
                                    className='px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Discard Changes
                                </button>
                                <button
                                    type='button'
                                    onClick={handleUnsavedChangesSave}
                                    disabled={saving}
                                    className='px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center'
                                >
                                    {saving ? (
                                        <>
                                            <Spinner size='sm' color='white' className='mr-2' />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save & Continue'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Puzzles;
